import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, Input, Row, Select, Space, Spin, Tag, Typography } from 'antd';
import { listCodeDictionary, validatePartNumber } from '../api';
import type { CodeDictionaryEntry, PartNumberSegments } from '../api';

/**
 * Inventory Phase 1.5 — Part Number Builder
 *
 * Standalone composer that loads the 4 required dimensions (class, item, color,
 * size) + softness + supplier from the code dictionary, renders Ant Design
 * Select dropdowns, and emits a composed part-number string back to the parent.
 *
 * Layout: 5 columns — Class / Item / Color + Variant / Size / Softness / Supplier.
 * The composed canonical format is `{class}-{item}-{color}{variant}-{size}`.
 * Softness and supplier are metadata only — they are NOT part of the composed
 * string (matches `partnumber.service.ts` + master-plan segment layout).
 *
 * The parent controls onChange; this component is fully controlled via the
 * `value` prop so it can sit inside an Ant Design `Form.Item`.
 */

const { Text } = Typography;

export interface PartNumberBuilderValue {
  classCode: string | null;
  itemCode: string | null;
  colorCode: string | null;
  colorVariant: string | null;
  sizeCode: string | null;
  softnessCode: string | null;
  supplierCode: string | null;
  partNumber: string; // composed (may be empty if any required segment missing)
}

interface Props {
  value?: PartNumberBuilderValue;
  onChange?: (value: PartNumberBuilderValue) => void;
  disabled?: boolean;
}

const EMPTY_VALUE: PartNumberBuilderValue = {
  classCode: null,
  itemCode: null,
  colorCode: null,
  colorVariant: null,
  sizeCode: null,
  softnessCode: null,
  supplierCode: null,
  partNumber: '',
};

function composePartNumber(v: PartNumberBuilderValue): string {
  if (!v.classCode || !v.itemCode || !v.colorCode || !v.colorVariant || !v.sizeCode) return '';
  return `${v.classCode}-${v.itemCode}-${v.colorCode}${v.colorVariant}-${v.sizeCode}`;
}

type DictionaryMap = Record<string, CodeDictionaryEntry[]>;

const PartNumberBuilder: React.FC<Props> = ({ value, onChange, disabled }) => {
  const [dict, setDict] = useState<DictionaryMap>({});
  const [loading, setLoading] = useState(false);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  const current = value ?? EMPTY_VALUE;

  // ── Load all 6 dimensions in parallel ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const dimensions: Array<'class' | 'item' | 'color' | 'size' | 'softness' | 'supplier'> = [
      'class',
      'item',
      'color',
      'size',
      'softness',
      'supplier',
    ];
    Promise.all(
      dimensions.map((d) => listCodeDictionary({ dimension: d, active: true }).then((r) => ({ d, entries: r.entries })))
    )
      .then((results) => {
        if (cancelled) return;
        const map: DictionaryMap = {};
        for (const { d, entries } of results) {
          map[d] = entries;
        }
        setDict(map);
      })
      .catch(() => {
        if (!cancelled) setDict({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Re-validate whenever the composed part-number changes ─────────────────
  useEffect(() => {
    const pn = current.partNumber;
    if (!pn) {
      setValidationIssues([]);
      setIsValid(null);
      return;
    }
    let cancelled = false;
    validatePartNumber(pn)
      .then((res) => {
        if (cancelled) return;
        setIsValid(res.valid);
        setValidationIssues(res.issues);
      })
      .catch(() => {
        if (cancelled) return;
        setIsValid(null);
        setValidationIssues([]);
      });
    return () => {
      cancelled = true;
    };
  }, [current.partNumber]);

  const update = useCallback(
    (patch: Partial<PartNumberBuilderValue>) => {
      const next: PartNumberBuilderValue = { ...current, ...patch };
      next.partNumber = composePartNumber(next);
      onChange?.(next);
    },
    [current, onChange]
  );

  const sortEntries = useCallback((entries: CodeDictionaryEntry[] | undefined) => {
    if (!entries) return [];
    return [...entries].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.code.localeCompare(b.code);
    });
  }, []);

  const classOptions = useMemo(() => sortEntries(dict.class), [dict.class, sortEntries]);
  const itemOptions = useMemo(() => sortEntries(dict.item), [dict.item, sortEntries]);
  const colorOptions = useMemo(() => sortEntries(dict.color), [dict.color, sortEntries]);
  const sizeOptions = useMemo(() => sortEntries(dict.size), [dict.size, sortEntries]);
  const softnessOptions = useMemo(() => sortEntries(dict.softness), [dict.softness, sortEntries]);
  const supplierOptions = useMemo(() => sortEntries(dict.supplier), [dict.supplier, sortEntries]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin tip="Loading code dictionary..." />
      </div>
    );
  }

  const renderOptions = (entries: CodeDictionaryEntry[]) =>
    entries.map((e) => (
      <Select.Option key={e.id} value={e.code}>
        <Space>
          <Text code>{e.code}</Text>
          <Text>{e.label}</Text>
        </Space>
      </Select.Option>
    ));

  return (
    <div>
      <Row gutter={8}>
        <Col span={4}>
          <Form.Item label="Class" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Class"
              disabled={disabled}
              value={current.classCode ?? undefined}
              onChange={(val) => update({ classCode: val ?? null })}
            >
              {renderOptions(classOptions)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Item" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Item"
              disabled={disabled}
              value={current.itemCode ?? undefined}
              onChange={(val) => update({ itemCode: val ?? null })}
            >
              {renderOptions(itemOptions)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="Color" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Color"
              disabled={disabled}
              value={current.colorCode ?? undefined}
              onChange={(val) => update({ colorCode: val ?? null })}
            >
              {renderOptions(colorOptions)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="Variant" style={{ marginBottom: 8 }} tooltip="Numeric color variant (001-999)">
            <Input
              placeholder="001"
              maxLength={3}
              disabled={disabled}
              value={current.colorVariant ?? ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                update({ colorVariant: raw || null });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="Size" style={{ marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Size"
              disabled={disabled}
              value={current.sizeCode ?? undefined}
              onChange={(val) => update({ sizeCode: val ?? null })}
            >
              {renderOptions(sizeOptions)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="Softness" style={{ marginBottom: 8 }} tooltip="Metadata only — not part of composed PN">
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="—"
              disabled={disabled}
              value={current.softnessCode ?? undefined}
              onChange={(val) => update({ softnessCode: val ?? null })}
            >
              {renderOptions(softnessOptions)}
            </Select>
          </Form.Item>
        </Col>
        <Col span={3}>
          <Form.Item label="Supplier" style={{ marginBottom: 8 }} tooltip="Metadata only — not part of composed PN">
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="—"
              disabled={disabled}
              value={current.supplierCode ?? undefined}
              onChange={(val) => update({ supplierCode: val ?? null })}
            >
              {renderOptions(supplierOptions)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <div style={{ marginTop: 8 }}>
        <Space>
          <Text type="secondary">Composed:</Text>
          {current.partNumber ? (
            <Text code strong>
              {current.partNumber}
            </Text>
          ) : (
            <Text type="secondary" italic>
              (fill all 5 required segments)
            </Text>
          )}
          {isValid === true && <Tag color="green">Valid</Tag>}
          {isValid === false && <Tag color="red">Invalid</Tag>}
        </Space>
        {validationIssues.length > 0 && (
          <Alert
            type="warning"
            style={{ marginTop: 6 }}
            showIcon
            message="Dictionary validation issues"
            description={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {validationIssues.map((i, idx) => (
                  <li key={idx}>{i}</li>
                ))}
              </ul>
            }
          />
        )}
      </div>
    </div>
  );
};

export default PartNumberBuilder;
